import React, { lazy, memo, Suspense } from 'react';
import { BrowserRouter, Outlet, Route, Routes } from 'react-router';
import rootRoutes from 'router/routes';
import { RouteParams } from 'router/routesPaths';
import { Layout } from 'components/layouts';
import RequireAuth from 'domain/auth/components/RequireAuth';
import LoadingSpinner from 'components/shared/LoadingSpinner';
import { useRemotes } from 'mfe/remotesContext';
import { useHostContextValue } from 'mfe/useHostContextValue';
import RemoteErrorBoundary from 'mfe/RemoteErrorBoundary';

const SignInPage = lazy(() => import('domain/auth/components/SignInPage'));

// Dérivé de la table réelle (pas de l'enum) : un chemin migré vers un remote
// reste dans RoutesPath pour la sidebar sans bloquer le montage du remote.
const hostPaths = new Set<string>(rootRoutes.map(({ path }) => path));

function generateRoutes(routes: RouteParams[]) {
  return routes.map(({ path, index, Element, children }, i) => {
    const element = (
      <RequireAuth>
        <Element />
      </RequireAuth>
    );
    return children && !index ? (
      <Route key={path + i} path={path} index={index} element={<Outlet />}>
        <Route index element={element} />
        {generateRoutes(children)}
      </Route>
    ) : (
      <Route key={path + i} path={path} index={index} element={element} />
    );
  });
}

const centeredSpinner = (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
    <LoadingSpinner />
  </div>
);

function AppRoutes() {
  const { remotes } = useRemotes();
  const hostContext = useHostContextValue();

  return (
    <Routes>
      <Route path={'/signIn'} element={<SignInPage />} />
      <Route element={<Layout />}>
        {generateRoutes(rootRoutes)}
        {remotes.map(({ routePath, label, Component }) => {
          if (hostPaths.has(routePath)) {
            console.warn(`[MFE] routePath "${routePath}" entre en collision avec une route host, remote ignoré`);
            return null;
          }
          return (
            <Route
              key={routePath}
              path={`${routePath}/*`}
              element={
                <RequireAuth>
                  <RemoteErrorBoundary name={label}>
                    <Suspense
                      fallback={
                        <div className="flex min-h-[60vh] items-center justify-center">
                          <LoadingSpinner />
                        </div>
                      }
                    >
                      <Component context={hostContext} />
                    </Suspense>
                  </RemoteErrorBoundary>
                </RequireAuth>
              }
            />
          );
        })}
      </Route>
    </Routes>
  );
}

function Router() {
  const { status } = useRemotes();

  return (
    <BrowserRouter>
      {status === 'loading' ? (
        // Tant que le manifeste charge, ne pas rendre les routes : un deep-link
        // vers un remote matcherait brievement le catch-all HomePage ('*').
        // Le gate vit HORS du Suspense : au passage a ready, ce spinner est
        // remplace net au lieu d'etre garde cache par le boundary (ce qui
        // mettrait deux spinners dans le DOM en meme temps).
        centeredSpinner
      ) : (
        <Suspense fallback={centeredSpinner}>
          <AppRoutes />
        </Suspense>
      )}
    </BrowserRouter>
  );
}

export default memo(Router);
